import request from 'axios';
import { api } from 'src/boot/axios';
import { defineStore } from 'pinia';
import { getResponseData, notifyError, notifySuccess } from 'utils';
import { setValidationErrors, resetErrors } from 'utils';
import { TNormalizedPeople, TPeoplePayload } from 'types';
import { trace } from 'src/boot/trace';

type State = {
  user: TNormalizedPeople;
  errors: null;
};

export const useProfileStore = defineStore('profile', {
  state: (): State => ({
    user: {} as TNormalizedPeople,
    errors: null,
  }),
  actions: {
    cacheProfileData(data: TNormalizedPeople) {
      if (data) {
        localStorage.setItem('profile', JSON.stringify(data));
        trace('Caching profile data:', 'info', data);
      }
    },
    async deleteProfilePhoto() {
      try {
        const response = await api.delete(
          `/api/v1/people/${this.user.id}/profile-image`,
        );
        if (response.status === 201) {
          notifySuccess('Your profile photo was removed successfully.');
          await this.getUser(true);
          return true;
        }
        notifyError(
          'There was a problem removing your profile photo was, a reload of the application may fix this.',
        );
        return false;
      } catch (err) {
        notifyError();
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
        return false;
      }
    },
    fetchUserData(oc = false, skipCacheFor: null | string[] = null) {
      let url = `/api/v1/full-profile`;
      const params = [];

      if (oc) {
        params.push('oc=1');
      }

      if (skipCacheFor && Array.isArray(skipCacheFor)) {
        skipCacheFor.forEach((item) => {
          params.push(`ocf=${item}`);
        });
      }

      if (params.length > 0) {
        url += '?' + params.join('&');
      }

      return api.get(url);
    },
    getGrandRanks() {
      return Array.isArray(this?.user?.masonic_history?.grand_ranks)
        ? this?.user?.masonic_history?.grand_ranks
        : [];
    },
    getHighestRanks() {
      return this?.user?.highest_ranks ?? [];
    },
    getHonoursPosts() {
      return Array.isArray(this?.user?.masonic_history?.honours_posts)
        ? this?.user?.masonic_history?.honours_posts
        : [];
    },
    getUnitOfficesPosts() {
      return this?.user?.masonic_history?.unit_offices_posts ?? [];
    },
    getUnits: function () {
      return Array.isArray(this?.user?.units) ? this.user.units : [];
    },
    profilePhotoURL(): string {
      let hash = 0;
      if (this.user?.image_url?.length && this.user?.image_path?.length) {
        const imagePath = this.user?.image_path;
        for (let i = 0; i < imagePath.length; i++) {
          const char = imagePath.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash |= 0; // Convert to 32bit integer
        }
        return [this.user?.image_url.replace('?undefined', ''), hash].join('?');
      }
      return '';
    },
    async getUpdateEmailCode(email: string) {
      let state = null;

      try {
        const response = await api.post(
          `/api/v1/update-people-email/get-code/${this.user.id}`,
          {
            email,
          },
        );

        resetErrors();
        state = response?.data?.state ?? state;

        return ['CODE_SENT'].includes(state);
      } catch (err) {
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
      }

      return false;
    },
    async getUser(skipCache = false, skipCacheFor: null | string[] = null) {
      // Load from cache.
      const cacheProfileData = localStorage.getItem('profile');
      if (cacheProfileData && !skipCache) {
        const data = JSON.parse(cacheProfileData) as TNormalizedPeople;
        trace('Cached profile data found:', 'info', data);
        this.setUser(data);
        return;
      }

      // No cache.
      trace('Fetching fresh profile data:', 'info');
      const data = await this.fetchUserData(skipCache, skipCacheFor);
      const profileData = getResponseData(data) as TNormalizedPeople;
      this.setUser(profileData);
      this.cacheProfileData(profileData);

      return;
    },
    setUser(data: any = {}) {
      this.user = {
        ...data,
        image_url: data?.image_url
          ? data.image_url + '?' + data?.last_update
          : null,
      };
    },
    async submitUpdateEmail(payload: any) {
      try {
        const response = await api.post(
          `/api/v1/update-people-email/update-email/${this.user.id}`,
          payload,
        );
        resetErrors();

        if (['UPDATED'].includes(response?.data?.state)) {
          notifySuccess('Your email address was updated successfully.');
          await this.getUser(true);
          return true;
        }

        notifyError(
          'Unfortunately, your email address could not be updated. A reload of the application may fix this.',
        );
        return false;
      } catch (err) {
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
      }

      return false;
    },
    async submitUpdateEmailCode(email: string, code: string) {
      let state = null;

      try {
        const response = await api.post(
          `/api/v1/update-people-email/validate-code/${this.user.id}`,
          {
            email,
            code,
          },
        );
        resetErrors();

        state = response?.data?.state ?? state;

        return ['VALID_CODE'].includes(state);
      } catch (err) {
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
      }
    },
    async updateDetails(payload: TPeoplePayload) {
      const { user, getUser } = useProfileStore();
      try {
        const response = await api.put(`/api/v1/people/${user.id}`, payload);
        if (response.status === 200) {
          notifySuccess('Your personal details were updated successfully.');
          await getUser(true);
          return true;
        }
        notifyError(
          'There was a problem updating your personal details, a reload of the application may fix this.',
        );
        return false;
      } catch (err) {
        notifyError();
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
        return false;
      }
    },
    async updateProfilePhoto(payload: { image?: Blob }) {
      if (!payload.image) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('image', payload.image);

        const response = await api.post(
          `/api/v1/people/${this.user.id}/profile-image`,
          formData,
        );

        if (response.status === 201) {
          notifySuccess('Your profile photo was updated successfully.');
          await this.getUser(true);
          return true;
        }
        notifyError(
          'There was a problem updating your profile photo was, a reload of the application may fix this.',
        );
        return false;
      } catch (err) {
        notifyError();
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
        return false;
      }
    },
    clear() {
      this.user = {} as TNormalizedPeople;
      this.errors = null;
    },
  },
  getters: {
    isMember(): boolean {
      return this.user?.active_units?.length > 0;
    },
    initials(): string {
      const forenames = this.user?.forenames || '';
      const surname = this.user?.surname || '';
      return forenames.charAt(0) + surname.charAt(0);
    },
  },
});
